import { useContext, useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { FiDownload, FiEdit2, FiPlus, FiTrash2, FiUploadCloud } from "react-icons/fi";
import { IoWarning } from "react-icons/io5";
import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from "react-icons/md";
import LogicContext from "../../Context/LogicContext";

const DOCUMENT_TYPES = [
  "Signed Offer Letter",
  "National ID (NID)",
  "Academic Certificate",
  "TIN Certificate",
  "Training Certificate",
  "Other",
];

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png"];
const PAGE_SIZES = [5, 10, 20];

const SAMPLE_DOCUMENTS = [
  { type: "Signed Offer Letter", fileName: "offer-letter.png", size: 31744, date: "2023-02-09", remark: "Original copy" },
  { type: "National ID (NID)", fileName: "national-id.png", size: 28672, date: "2023-02-09", remark: "Front and back" },
  { type: "Academic Certificate", fileName: "certificate.png", size: 45056, date: "2023-02-10", remark: "B.Sc. Computer Science" },
  { type: "TIN Certificate", fileName: "tin.png", size: 20480, date: "2023-02-10", remark: "-" },
  { type: "Training Certificate", fileName: "training.png", size: 33792, date: "2023-03-01", remark: "-" },
].map((document, index) => ({ ...document, id: index + 1, url: "" }));

function formatSize(bytes) {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatUploadDate(value) {
  const date = new Date(value);
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function ModalShell({ open, onClose, children }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          // Doubled selector so global dialog styles cannot stretch the modal.
          "&&": {
            width: "clamp(420px, 36vw, 700px)",
            maxWidth: "calc(100vw - 32px)",
            m: 2,
            borderRadius: "10px",
          },
        },
      }}
    >
      {children}
    </Dialog>
  );
}

function DocumentFormModal({ mode, initial, onSubmit, onClose }) {
  const [type, setType] = useState(initial?.type ?? "");
  const [remark, setRemark] = useState(initial?.remark === "-" ? "" : initial?.remark ?? "");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  function handleFile(event) {
    const chosen = event.target.files?.[0];
    event.target.value = "";
    if (!chosen) return;
    if (!ACCEPTED_TYPES.includes(chosen.type)) {
      setError("Only JPG or PNG files are allowed.");
      return;
    }
    if (chosen.size > MAX_FILE_BYTES) {
      setError("File must be 5MB or smaller.");
      return;
    }
    setError("");
    setFile(chosen);
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!type) return setError("Document type is required.");
    if (mode === "create" && !file) return setError("Please upload a file.");
    onSubmit({ type, remark: remark.trim(), file });
  }

  return (
    <ModalShell open onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="px-6 pt-6">
          <h2 className="text-xl font-bold text-gray-900">{mode === "edit" ? "Edit Document" : "Add Document"}</h2>
          <p className="mt-1 text-xs text-gray-500">Attach a scanned copy or digital file to this employee's record.</p>

          <label className="mt-5 block text-xs font-medium text-gray-800">Document type*</label>
          <select
            value={type}
            onChange={(event) => {
              setType(event.target.value);
              setError("");
            }}
            className="mt-1 h-10 w-full rounded-md border-0 bg-slate-100 px-3 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {DOCUMENT_TYPES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          <label className="mt-4 block text-xs font-medium text-gray-800">
            Upload File/Document{mode === "create" ? "*" : ""}
          </label>
          <div className="mt-1 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-gray-600">
                <FiUploadCloud className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">
                  {file ? file.name : initial?.fileName && mode === "edit" ? initial.fileName : "Upload your document"}
                </p>
                <p className="text-[11px] text-gray-500">JPG or PNG format · Max. 5MB</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="h-8 rounded-md border border-blue-500 px-4 text-xs font-medium text-blue-500 hover:bg-blue-50"
            >
              {file ? "Change" : "Upload"}
            </button>
            <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png" onChange={handleFile} className="hidden" />
          </div>

          <label className="mt-4 block text-xs font-medium text-gray-800">Remark</label>
          <input
            value={remark}
            onChange={(event) => setRemark(event.target.value)}
            maxLength={200}
            placeholder="Input Text"
            className="mt-1 h-10 w-full rounded-md bg-slate-100 px-3 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
        </div>
        <div className="mt-5 flex items-center justify-between border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-md bg-slate-100 px-5 text-xs font-medium text-gray-700 hover:bg-slate-200"
          >
            Cancel
          </button>
          <button type="submit" className="h-9 rounded-md bg-blue-600 px-5 text-xs font-medium text-white hover:bg-blue-700">
            {mode === "edit" ? "Save Changes" : "Save Document"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function DocumentViewModal({ document, onDownload, onClose }) {
  return (
    <ModalShell open onClose={onClose}>
      <div className="p-6">
        <h2 className="text-xl font-bold text-gray-900">{document.type}</h2>
        <p className="mt-1 text-[11px] text-gray-500">
          {document.type} · {document.fileName} · {formatSize(document.size)}
        </p>
        <div className="mt-5 flex h-[240px] w-full max-w-[300px] items-center justify-center overflow-hidden rounded bg-slate-100 text-xs font-medium text-gray-800">
          {document.url ? (
            <img src={document.url} alt={document.fileName} className="h-full w-full object-contain" />
          ) : (
            "Document Details"
          )}
        </div>
        <div className="mt-5 grid grid-cols-[90px_1fr] gap-2 text-xs">
          <span className="text-gray-500">Remark</span>
          <span className="font-medium text-gray-900">{document.remark || "-"}</span>
        </div>
        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={() => onDownload(document)}
            className="flex h-9 items-center gap-2 rounded-md bg-slate-100 px-4 text-xs font-medium text-blue-600 hover:bg-slate-200"
          >
            <FiDownload className="h-3.5 w-3.5" />
            Download
          </button>
          <button type="button" onClick={onClose} className="h-9 rounded-md bg-blue-600 px-5 text-xs font-medium text-white hover:bg-blue-700">
            Close
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

export function DocumentDeleteModal({ onConfirm, onClose }) {
  return (
    <ModalShell open onClose={onClose}>
      <div className="p-8">
        <IoWarning className="h-10 w-10 text-red-600" />
        <h2 className="mt-4 text-xl font-bold text-slate-700">Caution! Delete Item?</h2>
        <p className="mt-2 text-xs text-slate-600">Are you sure that you want to delete this item? It will be deleted forever.</p>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 rounded-md bg-slate-200 px-6 text-xs font-medium text-slate-600 hover:bg-slate-300">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} className="h-9 rounded-md bg-red-600 px-6 text-xs font-medium text-white hover:bg-red-700">
            Delete
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

export function PagerButton({ onClick, disabled, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="flex h-7 w-7 items-center justify-center rounded text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}

function EmployeeDocumentsTab() {
  const logicContext = useContext(LogicContext);
  const [documents, setDocuments] = useState(SAMPLE_DOCUMENTS);
  const [modal, setModal] = useState(null); // { kind: "add" | "edit" | "view" | "delete", document? }
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [page, setPage] = useState(1);
  const nextId = useRef(SAMPLE_DOCUMENTS.length + 1);
  const documentsRef = useRef(documents);
  documentsRef.current = documents;

  // Release any object URLs created for uploaded files when the tab unmounts.
  useEffect(
    () => () => {
      documentsRef.current.forEach((document) => document.url && URL.revokeObjectURL(document.url));
    },
    []
  );

  const pageCount = Math.max(1, Math.ceil(documents.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const rows = documents.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const firstIndex = (currentPage - 1) * pageSize;

  function handleAdd({ type, remark, file }) {
    const record = {
      id: nextId.current++,
      type,
      remark: remark || "-",
      fileName: file.name,
      size: file.size,
      date: new Date().toISOString(),
      url: URL.createObjectURL(file),
    };
    setDocuments((current) => [...current, record]);
    setModal(null);
    logicContext.showToast("Document added successfully", "success");
  }

  function handleEdit({ type, remark, file }) {
    const target = modal.document;
    setDocuments((current) =>
      current.map((document) => {
        if (document.id !== target.id) return document;
        const next = { ...document, type, remark: remark || "-" };
        if (file) {
          if (document.url) URL.revokeObjectURL(document.url);
          Object.assign(next, { fileName: file.name, size: file.size, url: URL.createObjectURL(file) });
        }
        return next;
      })
    );
    setModal(null);
    logicContext.showToast("Document updated successfully", "success");
  }

  function handleDelete() {
    const target = modal.document;
    if (target.url) URL.revokeObjectURL(target.url);
    setDocuments((current) => current.filter((document) => document.id !== target.id));
    setModal(null);
    logicContext.showToast("Document deleted successfully", "success");
  }

  function handleDownload(document) {
    if (!document.url) {
      logicContext.showToast("No file is attached to this sample document", "info");
      return;
    }
    const link = window.document.createElement("a");
    link.href = document.url;
    link.download = document.fileName;
    link.click();
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Documents/Attachments</h2>
        <button
          type="button"
          onClick={() => setModal({ kind: "add" })}
          className="flex items-center gap-1 border-b border-blue-500 pb-0.5 text-xs font-medium text-blue-500"
        >
          <FiPlus className="h-3.5 w-3.5" />
          Add New
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[680px] text-left text-xs">
          <thead>
            <tr className="bg-slate-100 text-gray-800">
              <th className="w-12 rounded-l-md px-3 py-3 font-semibold">#</th>
              <th className="px-3 py-3 font-semibold">Document Type</th>
              <th className="px-3 py-3 font-semibold">Date Uploaded</th>
              <th className="px-3 py-3 font-semibold">Size</th>
              <th className="px-3 py-3 font-semibold">Remark</th>
              <th className="rounded-r-md px-3 py-3 text-center font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-sm text-gray-500">
                  No documents uploaded yet.
                </td>
              </tr>
            ) : (
              rows.map((document, index) => (
                <tr
                  key={document.id}
                  onClick={() => setModal({ kind: "view", document })}
                  className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-slate-50"
                >
                  <td className="px-3 py-4 text-gray-800">{firstIndex + index + 1}</td>
                  <td className="px-3 py-4 font-medium text-gray-900">{document.type}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-gray-800">{formatUploadDate(document.date)}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-gray-800">{formatSize(document.size)}</td>
                  <td className="px-3 py-4 text-gray-800">{document.remark}</td>
                  <td className="px-3 py-4" onClick={(event) => event.stopPropagation()}>
                    <div className="flex items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => setModal({ kind: "edit", document })}
                        aria-label={`Edit ${document.type}`}
                        className="text-gray-700 hover:text-blue-500"
                      >
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownload(document)}
                        aria-label={`Download ${document.type}`}
                        className="text-gray-700 hover:text-blue-500"
                      >
                        <FiDownload className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setModal({ kind: "delete", document })}
                        aria-label={`Delete ${document.type}`}
                        className="rounded bg-red-50 p-1.5 text-red-500 hover:bg-red-100"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
        <p>{documents.length} document(s)</p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <label className="flex items-center gap-2">
            Rows per page
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className="h-8 w-16 rounded border border-gray-200 bg-slate-50 px-2 text-xs text-gray-800 focus:outline-none"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <span>
            Page {currentPage} of {pageCount}
          </span>
          <div className="flex items-center">
            <PagerButton label="First page" onClick={() => setPage(1)} disabled={currentPage === 1}>
              <MdKeyboardDoubleArrowLeft className="h-4 w-4" />
            </PagerButton>
            <PagerButton label="Previous page" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>
              <MdKeyboardArrowLeft className="h-4 w-4" />
            </PagerButton>
            <PagerButton label="Next page" onClick={() => setPage(currentPage + 1)} disabled={currentPage === pageCount}>
              <MdKeyboardArrowRight className="h-4 w-4" />
            </PagerButton>
            <PagerButton label="Last page" onClick={() => setPage(pageCount)} disabled={currentPage === pageCount}>
              <MdKeyboardDoubleArrowRight className="h-4 w-4" />
            </PagerButton>
          </div>
        </div>
      </div>

      {modal?.kind === "add" ? <DocumentFormModal mode="create" onSubmit={handleAdd} onClose={() => setModal(null)} /> : null}
      {modal?.kind === "edit" ? (
        <DocumentFormModal mode="edit" initial={modal.document} onSubmit={handleEdit} onClose={() => setModal(null)} />
      ) : null}
      {modal?.kind === "view" ? (
        <DocumentViewModal document={modal.document} onDownload={handleDownload} onClose={() => setModal(null)} />
      ) : null}
      {modal?.kind === "delete" ? <DocumentDeleteModal onConfirm={handleDelete} onClose={() => setModal(null)} /> : null}
    </>
  );
}

export default EmployeeDocumentsTab;
